import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { memo, useCallback, useEffect, useRef } from "react";
import { cn } from "../../lib/utils";
import { animate } from "framer-motion";
/**
 * Glowing Effect Component Suite (Layer 1 Primitive)
 *
 * A collection of advanced visual effects using framer-motion:
 * - GlowingEffect: Animated border with gradient tracking
 * - CardTilt: 3D tilt effect following mouse position
 * - HoloBackground: Holographic background effect
 * - HoloImage: Holographic effect for images/icons
 *
 * Advanced Framer Motion Techniques:
 * - animate() function for programmatic animations
 * - Custom easing curves [0.16, 1, 0.3, 1]
 * - requestAnimationFrame for performance
 * - CSS custom properties integration
 * - Mouse tracking with proximity detection
 *
 * @example
 * ```tsx
 * <CardTilt>
 *   <div className="card">
 *     <GlowingEffect variant="game-primitive" proximity={120} />
 *     <HoloBackground variant="game-primitive" />
 *     <HoloImage variant="game-primitive" />
 *   </div>
 * </CardTilt>
 * ```
 */
// Shared gradient generator
const getGradient = (variant = "default") => {
    if (variant === "white") {
        return `repeating-conic-gradient(
      from 236.84deg at 50% 50%,
      var(--black),
      var(--black) calc(25% / var(--repeating-conic-gradient-times))
    )`;
    }
    if (variant === "game-primitive") {
        // Using actual game primitive colors: player, canon, goal, memory
        return `radial-gradient(circle, #EC4899 10%, #EC489900 20%),
      radial-gradient(circle at 40% 40%, #F59E0B 5%, #F59E0B00 15%),
      radial-gradient(circle at 60% 60%, #10B981 10%, #10B98100 20%),
      radial-gradient(circle at 40% 60%, #3B82F6 10%, #3B82F600 20%),
      repeating-conic-gradient(
        from 236.84deg at 50% 50%,
        #EC4899 0%,
        #F59E0B calc(25% / var(--repeating-conic-gradient-times)),
        #10B981 calc(50% / var(--repeating-conic-gradient-times)),
        #3B82F6 calc(75% / var(--repeating-conic-gradient-times)),
        #EC4899 calc(100% / var(--repeating-conic-gradient-times))
      )`;
    }
    // default variant
    return `radial-gradient(circle, #dd7bbb 10%, #dd7bbb00 20%),
    radial-gradient(circle at 40% 40%, #d79f1e 5%, #d79f1e00 15%),
    radial-gradient(circle at 60% 60%, #5a922c 10%, #5a922c00 20%),
    radial-gradient(circle at 40% 60%, #4c7894 10%, #4c789400 20%),
    repeating-conic-gradient(
      from 236.84deg at 50% 50%,
      #dd7bbb 0%,
      #d79f1e calc(25% / var(--repeating-conic-gradient-times)),
      #5a922c calc(50% / var(--repeating-conic-gradient-times)),
      #4c7894 calc(75% / var(--repeating-conic-gradient-times)),
      #dd7bbb calc(100% / var(--repeating-conic-gradient-times))
    )`;
};
// Original GlowingEffect - Animated border effect (EXACTLY as before)
const GlowingEffect = memo(({ blur = 0, inactiveZone = 0.7, proximity = 0, spread = 20, variant = "default", glow = false, className, movementDuration = 2, borderWidth = 1, disabled = true, }) => {
    const containerRef = useRef(null);
    const lastPosition = useRef({ x: 0, y: 0 });
    const animationFrameRef = useRef(0);
    const handleMove = useCallback((e) => {
        if (!containerRef.current)
            return;
        if (animationFrameRef.current) {
            cancelAnimationFrame(animationFrameRef.current);
        }
        animationFrameRef.current = requestAnimationFrame(() => {
            const element = containerRef.current;
            if (!element)
                return;
            const { left, top, width, height } = element.getBoundingClientRect();
            const mouseX = e?.x ?? lastPosition.current.x;
            const mouseY = e?.y ?? lastPosition.current.y;
            if (e) {
                lastPosition.current = { x: mouseX, y: mouseY };
            }
            const centerX = left + width * 0.5;
            const centerY = top + height * 0.5;
            const distanceFromCenter = Math.hypot(mouseX - centerX, mouseY - centerY);
            const inactiveRadius = 0.5 * Math.min(width, height) * inactiveZone;
            if (distanceFromCenter < inactiveRadius) {
                element.style.setProperty("--active", "0");
                return;
            }
            const isActive = mouseX > left - proximity &&
                mouseX < left + width + proximity &&
                mouseY > top - proximity &&
                mouseY < top + height + proximity;
            element.style.setProperty("--active", isActive ? "1" : "0");
            if (!isActive)
                return;
            const currentAngle = parseFloat(element.style.getPropertyValue("--start")) || 0;
            let targetAngle = (180 * Math.atan2(mouseY - centerY, mouseX - centerX)) /
                Math.PI +
                90;
            const angleDiff = ((targetAngle - currentAngle + 180) % 360) - 180;
            const newAngle = currentAngle + angleDiff;
            animate(currentAngle, newAngle, {
                duration: movementDuration,
                ease: [0.16, 1, 0.3, 1],
                onUpdate: (value) => {
                    element.style.setProperty("--start", String(value));
                },
            });
        });
    }, [inactiveZone, proximity, movementDuration]);
    useEffect(() => {
        if (disabled)
            return;
        const handleScroll = () => handleMove();
        const handlePointerMove = (e) => handleMove(e);
        window.addEventListener("scroll", handleScroll, { passive: true });
        document.body.addEventListener("pointermove", handlePointerMove, {
            passive: true,
        });
        return () => {
            if (animationFrameRef.current) {
                cancelAnimationFrame(animationFrameRef.current);
            }
            window.removeEventListener("scroll", handleScroll);
            document.body.removeEventListener("pointermove", handlePointerMove);
        };
    }, [handleMove, disabled]);
    return (_jsxs(_Fragment, { children: [_jsx("div", { className: cn("pointer-events-none absolute -inset-px hidden rounded-[inherit] border opacity-0 transition-opacity", glow && "opacity-100", variant === "white" && "border-white", disabled && "!block") }), _jsx("div", { ref: containerRef, style: {
                    "--blur": `${blur}px`,
                    "--spread": spread,
                    "--start": "0",
                    "--active": "0",
                    "--glowingeffect-border-width": `${borderWidth}px`,
                    "--repeating-conic-gradient-times": "5",
                    "--gradient": getGradient(variant),
                }, className: cn("pointer-events-none absolute inset-0 rounded-[inherit] opacity-100 transition-opacity", glow && "opacity-100", blur > 0 && "blur-[var(--blur)] ", className, disabled && "!hidden"), children: _jsx("div", { className: cn("glow", "rounded-[inherit]", 'after:content-[""] after:rounded-[inherit] after:absolute after:inset-[calc(-1*var(--glowingeffect-border-width))]', "after:[border:var(--glowingeffect-border-width)_solid_transparent]", "after:[background:var(--gradient)] after:[background-attachment:fixed]", "after:opacity-[var(--active)] after:transition-opacity after:duration-300", "after:[mask-clip:padding-box,border-box]", "after:[mask-composite:intersect]", "after:[mask-image:linear-gradient(#0000,#0000),conic-gradient(from_calc((var(--start)-var(--spread))*1deg),#00000000_0deg,#fff,#00000000_calc(var(--spread)*2deg))]") }) })] }));
});
GlowingEffect.displayName = "GlowingEffect";
// CardTilt - Adds 3D tilt effect to cards
export const CardTilt = memo(({ children, className, disabled = false, tiltFactor = 0.4 }) => {
    const isPointerInside = useRef(false);
    const refElement = useRef(null);
    if (disabled) {
        return _jsx(_Fragment, { children: children });
    }
    return (_jsx("div", { ref: refElement, className: cn("relative [contain:layout_style] [perspective:600px] transition-transform duration-300 ease-[cubic-bezier(0.16,1,0.3,1)] will-change-transform", className), style: {
            "--r-x": "0deg",
            "--r-y": "0deg",
        }, onPointerMove: (event) => {
            if (!refElement.current)
                return;
            const rect = event.currentTarget.getBoundingClientRect();
            const position = {
                x: event.clientX - rect.left,
                y: event.clientY - rect.top,
            };
            const percentage = {
                x: (100 / rect.width) * position.x,
                y: (100 / rect.height) * position.y,
            };
            const delta = {
                x: percentage.x - 50,
                y: percentage.y - 50,
            };
            const rotateX = -(delta.x / 3.5) * tiltFactor;
            const rotateY = (delta.y / 2) * tiltFactor;
            refElement.current.style.setProperty("--r-x", `${rotateX}deg`);
            refElement.current.style.setProperty("--r-y", `${rotateY}deg`);
        }, onPointerEnter: () => {
            isPointerInside.current = true;
        }, onPointerLeave: () => {
            isPointerInside.current = false;
            if (refElement.current) {
                refElement.current.style.setProperty("--r-x", "0deg");
                refElement.current.style.setProperty("--r-y", "0deg");
            }
        }, children: _jsx("div", { className: "h-full origin-center transition-transform duration-300 ease-[cubic-bezier(0.16,1,0.3,1)]", style: {
                transform: "rotateY(var(--r-x)) rotateX(var(--r-y))",
            }, children: children }) }));
});
CardTilt.displayName = "CardTilt";
// HoloBackground - Subtle holographic effect on the BACK of the card
export const HoloBackground = memo(({ variant = "default", className, disabled = false, opacity = 0.08, }) => {
    const containerRef = useRef(null);
    useEffect(() => {
        if (disabled || !containerRef.current)
            return;
        const handleMove = (e) => {
            if (!containerRef.current)
                return;
            const rect = containerRef.current.getBoundingClientRect();
            const x = ((e.clientX - rect.left) / rect.width) * 100;
            const y = ((e.clientY - rect.top) / rect.height) * 100;
            containerRef.current.style.setProperty("--m-x", `${x}%`);
            containerRef.current.style.setProperty("--m-y", `${y}%`);
        };
        document.body.addEventListener("pointermove", handleMove, { passive: true });
        return () => {
            document.body.removeEventListener("pointermove", handleMove);
        };
    }, [disabled]);
    if (disabled)
        return null;
    return (_jsx("div", { ref: containerRef, style: {
            "--m-x": "50%",
            "--m-y": "50%",
            "--opacity": opacity,
            "--repeating-conic-gradient-times": "5",
            "--gradient": getGradient(variant),
        }, className: cn("pointer-events-none absolute inset-0 rounded-[inherit] opacity-0 transition-opacity duration-300 group-hover:opacity-100", className), children: _jsx("div", { className: cn("absolute inset-0 rounded-[inherit]", "opacity-[var(--opacity)]", "[background:var(--gradient)] [background-attachment:fixed]", "[background-position:var(--m-x)_var(--m-y)]", "blur-[20px]") }) }));
});
HoloBackground.displayName = "HoloBackground";
// HoloImage - Holographic effect ONLY for icon squares
export const HoloImage = memo(({ variant = "default", className, disabled = false, proximity = 80, spread = 25, movementDuration = 0.6, opacity = 0.4, }) => {
    const containerRef = useRef(null);
    const lastPosition = useRef({ x: 0, y: 0 });
    const animationFrameRef = useRef(0);
    const handleMove = useCallback((e) => {
        if (!containerRef.current)
            return;
        if (animationFrameRef.current) {
            cancelAnimationFrame(animationFrameRef.current);
        }
        animationFrameRef.current = requestAnimationFrame(() => {
            const element = containerRef.current;
            if (!element)
                return;
            const { left, top, width, height } = element.getBoundingClientRect();
            const mouseX = e?.x ?? lastPosition.current.x;
            const mouseY = e?.y ?? lastPosition.current.y;
            if (e) {
                lastPosition.current = { x: mouseX, y: mouseY };
            }
            const centerX = left + width * 0.5;
            const centerY = top + height * 0.5;
            const isActive = mouseX > left - proximity &&
                mouseX < left + width + proximity &&
                mouseY > top - proximity &&
                mouseY < top + height + proximity;
            element.style.setProperty("--active", isActive ? "1" : "0");
            if (!isActive)
                return;
            const currentAngle = parseFloat(element.style.getPropertyValue("--start")) || 0;
            let targetAngle = (180 * Math.atan2(mouseY - centerY, mouseX - centerX)) / Math.PI + 90;
            const angleDiff = ((targetAngle - currentAngle + 180) % 360) - 180;
            const newAngle = currentAngle + angleDiff;
            animate(currentAngle, newAngle, {
                duration: movementDuration,
                ease: [0.16, 1, 0.3, 1],
                onUpdate: (value) => {
                    element.style.setProperty("--start", String(value));
                },
            });
        });
    }, [proximity, movementDuration]);
    useEffect(() => {
        if (disabled)
            return;
        const handleScroll = () => handleMove();
        const handlePointerMove = (e) => handleMove(e);
        window.addEventListener("scroll", handleScroll, { passive: true });
        document.body.addEventListener("pointermove", handlePointerMove, {
            passive: true,
        });
        return () => {
            if (animationFrameRef.current) {
                cancelAnimationFrame(animationFrameRef.current);
            }
            window.removeEventListener("scroll", handleScroll);
            document.body.removeEventListener("pointermove", handlePointerMove);
        };
    }, [handleMove, disabled]);
    if (disabled)
        return null;
    return (_jsx("div", { ref: containerRef, style: {
            "--spread": spread,
            "--start": "0",
            "--active": "0",
            "--opacity": opacity,
            "--repeating-conic-gradient-times": "5",
            "--gradient": getGradient(variant),
        }, className: cn("pointer-events-none absolute inset-0 rounded-[inherit]", className), children: _jsx("div", { className: cn("absolute inset-0 rounded-[inherit]", "opacity-[calc(var(--active)*var(--opacity))] transition-opacity duration-200", "[background:var(--gradient)] [background-attachment:fixed]", "[mask-image:conic-gradient(from_calc((var(--start)-var(--spread))*1deg),#00000000_0deg,#fff,#00000000_calc(var(--spread)*2deg))]", "mix-blend-overlay") }) }));
});
HoloImage.displayName = "HoloImage";
export { GlowingEffect };
//# sourceMappingURL=GlowingEffect.js.map